import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { TimesheetOvertimeRequestDocument } from 'generated/resources/graphql'
import chunk from 'lodash/chunk'

import { useAsyncData, useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSubscribe from 'modules/core/hooks/useSubscribe'
import { dateToString, getNormalizedToday } from 'modules/core/lib/dates'
import { EventTypes } from 'modules/core/lib/pubSub'
import companies from 'modules/core/store/collections/companies'

import { ShiftVersionHistoriesProvider } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/shiftVersionHistoriesContext'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { GroupByTypes } from 'modules/attendance/components/ClockIn/types'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { OvertimeRequestsProvider } from 'modules/attendance/lib/overtimeRequestsContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import BalanceUtils from 'modules/attendance/lib/utils/BalanceUtils'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import { ShiftVersionHistories } from 'modules/attendance/store/collections/shiftVersionHistories'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { EmployeeProjectsTrackingProvider } from 'modules/project_management/contexts/EmployeeProjectsTrackingContext'
import { useGetEmployeeProjectsTrackingByMonth } from 'modules/project_management/hooks/useGetEmployeeProjectsTrackingByMonth'
import { TeamsShiftLocationProvider } from 'modules/teams/components/teamsShiftLocationContext'
import teamsShiftLocation from 'modules/teams/store/collections/teamsShiftLocation'

import BalanceCounter from './BalanceCounter'
import ExtraHoursCounter from './ExtraHoursCounter'
import HourlyCounterWithTimeCategories from './HourlyCounterWithTimeCategories'
import HourlyTable from './HourlyTable'
import TimesheetBalanceSidepanel from './TimesheetBalanceSidepanel'

interface Props {
  store: Store
  showHourBalance: boolean
}

const VERSION_CHUNK_SIZE = 120

const HourlyShifts = ({ store, showHourBalance }: Props) => {
  const shiftVersionHistories = React.useRef(new ShiftVersionHistories()).current
  const { employeesSettings, shifts, balances } = useTimesheetData()
  const [showSidepanel, setShowSidepanel] = React.useState(false)

  const {
    stringDateFrom: startOn,
    stringDateTo: endOn,
    dateFrom: dateStartOn,
    dateTo: dateEndOn,
  } = useAttendanceDateNavigator()

  const normalizedToday = getNormalizedToday()
  const calculationToDate = normalizedToday < dateEndOn ? normalizedToday : dateEndOn

  const balance = BalanceUtils.aggregateDailyBalanceFromPlanning({
    balances,
    from: dateStartOn,
    to: calculationToDate,
  })

  const shiftsIds = AttendanceShiftUtils.shiftsIds(shifts)
  const shiftsIdsStr = shiftsIds.join('-')

  const { has_clock_in_location_enabled: hasLocationEnabled } = companies.current.attributes
  const hasOvertimeRequests = EmployeesSettingsUtils.hasOvertimeAuthorization(employeesSettings)

  const { data } = useQuery(TimesheetOvertimeRequestDocument, {
    variables: {
      employeeIds: [store.employeeId],
      startOn,
      endOn,
      includeApprovalFlow: true,
    },
    skip: !hasOvertimeRequests,
  })

  const overtimeRequests = data?.attendance?.overtimeRequests ?? []

  useUnsafeAsyncData(() => {
    const batches = chunk(shiftsIds, VERSION_CHUNK_SIZE).map((group: number[], i: number) => {
      const dataEmployee = { shift_ids: group, employee_id: store.employeeId }

      if (i === 0) {
        return {
          data: dataEmployee,
        }
      }

      return {
        data: dataEmployee,
        add: true,
        remove: false,
      }
    })

    return Promise.all(batches.map((dataBatch) => shiftVersionHistories.fetch(dataBatch)))
  }, [shiftsIdsStr])

  const { featureAvailability: hasNewProjects } = useAttendanceFeatures(
    Features.projects_management
  )
  const { featureAvailability: subprojectsEnabled } = useAttendanceFeatures(
    Features.projects_with_subprojects
  )
  const { featureAvailability: hasExtraHourManagementV2 } = useAttendanceFeatures(
    'dev_time_tracking_extra_hour_management_v2'
  )

  const fetchShiftLocations = () => {
    const storeCurrentMonth = store.currentMonth.clone()

    if (hasLocationEnabled) {
      teamsShiftLocation.fetch({
        data: {
          from_date: dateToString(storeCurrentMonth.startOf('month'), 'backend'),
          to_date: dateToString(storeCurrentMonth.endOf('month'), 'backend'),
          ids: [store.employeeId],
          group_by: GroupByTypes.employee,
        },
      })
    }
  }

  useSubscribe([EventTypes.ATTENDANCE_SHIFT_SAVED, EventTypes.ATTENDANCE_SHIFT_TOGGLED], () => {
    fetchShiftLocations()
  })

  useAsyncData(() => {
    const promises = []

    fetchShiftLocations()

    if (hasNewProjects) {
      promises.push(
        policyAuthorizations.authorizePolicies('projectmanagement.timerecord.create_records', [
          {
            id: store.employeeId,
            policyTargetClass: '::Employee',
          },
        ])
      )
    }

    return Promise.all(promises)
  }, [])

  const {
    queryData: {
      projectsAssigned,
      timeRecords,
      hasProjectsToTrack,
      fetchingTimeRecords,
      fetchingProjectWorkers,
      refetchEmployeeTimeRecords,
    },
  } = useGetEmployeeProjectsTrackingByMonth({
    employeeId: Number(store.employeeId),
    startDate: dateStartOn,
    endDate: dateEndOn,
    includeSubprojects: subprojectsEnabled,
    skip: !hasNewProjects,
  })

  return (
    <>
      {hasExtraHourManagementV2 ? (
        <Box paddingBottom="s24" gap="s24" flexDirection="row" width="full">
          <Box flexGrow className="flex-1">
            <BalanceCounter balance={balance} onClick={() => setShowSidepanel(true)} />
          </Box>
          <Box flexGrow className="flex-1">
            <ExtraHoursCounter />
          </Box>
        </Box>
      ) : (
        <>
          <Box paddingBottom="s24" gap="s24" flexDirection="row" width="full">
            <TeamsShiftLocationProvider collection={teamsShiftLocation}>
              <HourlyCounterWithTimeCategories showHourBalance={showHourBalance} />
            </TeamsShiftLocationProvider>
          </Box>
        </>
      )}
      {hasExtraHourManagementV2 && showSidepanel && (
        <TimesheetBalanceSidepanel onClose={() => setShowSidepanel(false)} shifts={shifts} />
      )}
      <ShiftVersionHistoriesProvider collection={shiftVersionHistories}>
        <OvertimeRequestsProvider collection={overtimeRequests}>
          {hasNewProjects ? (
            <EmployeeProjectsTrackingProvider
              projectsAssigned={projectsAssigned}
              timeRecords={timeRecords}
              hasProjectsToTrack={hasProjectsToTrack}
              fetchingTimeRecords={fetchingTimeRecords}
              fetchingProjectWorkers={fetchingProjectWorkers}
              refetchEmployeeTimeRecords={refetchEmployeeTimeRecords}
            >
              <HourlyTable store={store} showHourBalance={showHourBalance} />
            </EmployeeProjectsTrackingProvider>
          ) : (
            <HourlyTable store={store} showHourBalance={showHourBalance} />
          )}
        </OvertimeRequestsProvider>
      </ShiftVersionHistoriesProvider>
    </>
  )
}

export default HourlyShifts
